import { messageLinks } from "./links";
import { navigate } from "./router";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { parse, parseInline, type Block, type Inline } from "./format";
import { Emoji } from "./components/Emoji";
import { t } from "./i18n";

// Search snippets mark matches with these private-use characters, as Slack does.
const highlightStart = "";
const highlightEnd = "";
const highlighted = new RegExp(`(${highlightStart}[^${highlightEnd}]*${highlightEnd})`);

type Names = {
  name: (id: string) => string;
  me?: string;
  group?: (id: string) => { handle: string; mine: boolean } | undefined;
  // channel names a channel, for links to its messages.
  channel?: (id: string) => string;
  // plain is for text inside a control, such as a search result: mentions
  // and links are not controls of their own there.
  plain?: boolean;
};

function inline(nodes: Inline[], n: Names): ReactNode[] {
  return nodes.map((x, i) => {
    switch (x.t) {
      case "text":
        return <Fragment key={i}>{x.v}</Fragment>;
      case "br":
        return <br key={i} />;
      case "code":
        return <code key={i}>{x.v}</code>;
      case "mention":
        return (
          <span key={i} className={x.id === n.me ? "mention me" : "mention"} {...(n.plain ? {} : { "data-profile": x.id, role: "button", tabIndex: 0 })}>
            @{n.name(x.id)}
          </span>
        );
      case "group": {
        const g = n.group?.(x.id);
        return (
          <span key={i} className={`mention group ${g?.mine ? "me" : ""}`} title={t("A user group")}>
            @{g?.handle ?? x.label}
          </span>
        );
      }
      case "link": {
        if (n.plain) return <Fragment key={i}>{inline(x.children, n)}</Fragment>;
        // A link to a message here opens in place, and a bare one says
        // where the message is rather than its long address.
        const here = messageLinks(x.href, location.origin, 1)[0];
        if (here && x.href.startsWith(location.origin + "/")) {
          const bare = x.children.length === 1 && x.children[0].t === "text" && x.children[0].v === x.href;
          const where = n.channel?.(here.channel);
          return (
            <a
              key={i}
              href={x.href}
              className="message-link"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                navigate(x.href.slice(location.origin.length));
              }}
            >
              {bare ? (where ? t("a message in {channel}", { channel: where }) : t("a message in a channel")) : inline(x.children, n)}
            </a>
          );
        }
        return (
          <a key={i} href={x.href} target="_blank" rel="noopener noreferrer">
            {inline(x.children, n)}
          </a>
        );
      }
      case "emoji":
        return <Emoji key={i} name={x.name} />;
      case "b":
        return <strong key={i}>{inline(x.children, n)}</strong>;
      case "i":
        return <em key={i}>{inline(x.children, n)}</em>;
      case "s":
        return <del key={i}>{inline(x.children, n)}</del>;
    }
  });
}

function blocks(list: Block[], n: Names): ReactNode[] {
  return list.map((b, i) => {
    switch (b.t) {
      case "p":
        return <p key={i}>{inline(b.children, n)}</p>;
      case "pre":
        return <Code key={i} code={b.v} lang={b.lang} />;
      case "quote":
        return <blockquote key={i}>{blocks(b.children, n)}</blockquote>;
      case "ul":
        return (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j}>{inline(it, n)}</li>
            ))}
          </ul>
        );
      case "ol":
        return (
          <ol key={i} start={b.start}>
            {b.items.map((it, j) => (
              <li key={j}>{inline(it, n)}</li>
            ))}
          </ol>
        );
    }
  });
}

// RichText renders a message's formatted text (see format.ts); inline keeps
// it to one run of plain text, for previews inside a control.
export function RichText({ text, name, me, group, channel, inline: oneRun }: { text: string; inline?: boolean } & Names) {
  const n = { name, me, group, channel, plain: oneRun };
  return <>{oneRun ? inline(parseInline(text), n) : blocks(parse(text), n)}</>;
}

// Snippet renders a search snippet: matches as <mark>, and the rest as one
// run of formatted text.
export function Snippet({ text, name, me }: { text: string } & Names) {
  return (
    <>
      {text.split(highlighted).map((p, i) =>
        p.startsWith(highlightStart) ? <mark key={i}>{p.slice(1, -1)}</mark> : <RichText key={i} text={p} name={name} me={me} inline />,
      )}
    </>
  );
}

// Code is a code block, highlighted once the highlighter has loaded when it
// names a language; until then, and for any other, plain.
let highlighter: Promise<typeof import("./highlight")> | undefined;

function Code({ code, lang }: { code: string; lang?: string }) {
  const [html, setHTML] = useState<string>();
  useEffect(() => {
    if (!lang) return;
    let live = true;
    highlighter ??= import("./highlight");
    highlighter.then((h) => live && setHTML(h.highlight(code, lang)), () => {});
    return () => {
      live = false;
    };
  }, [code, lang]);
  return (
    <pre data-lang={html ? lang : undefined}>
      {html ? <code className="hljs" dangerouslySetInnerHTML={{ __html: html }} /> : <code>{code}</code>}
    </pre>
  );
}
